import { getArticleDetailHref } from '@/components/links/article'
import { getCaseStudiesDetailHref } from '@/components/links/case'
import { Article } from '@/entities/article'
import {
  BannerCarousel,
  Banner as BannerEntity,
  BannerTarget,
} from '@/entities/banner'
import { CaseStudies } from '@/entities/caseStudies'
import { Menus } from '@/entities/menus'
import { Team } from '@/entities/teams'
import {
  ClassConstructor,
  instanceToPlain,
  plainToInstance,
  TargetMap,
} from 'class-transformer'
import clsx from 'clsx'
import dayjs from 'dayjs'
import 'dayjs/locale/en-nz'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { camelCase, each, isPlainObject } from 'lodash-es'
import { Metadata } from 'next'
import { twMerge } from 'tailwind-merge'
dayjs.locale('en-nz')
dayjs.extend(timezone)
dayjs.extend(utc)

/**
 * Utility function for merging Tailwind CSS class names.
 * @param inputs Class values to merge
 * @returns Merged class name string
 */
export function cn(...inputs: Parameters<typeof clsx>) {
  return twMerge(clsx(inputs))
}

/**
 * Formats data into an instance of a class.
 * @param ClassCtor Class constructor
 * @param targetMaps Optional target maps for class transformation
 * @returns Function that formats data into an instance of the class
 */
export function formatInstance<T>(
  ClassCtor: ClassConstructor<T>,
  targetMaps?: TargetMap[]
) {
  return (data: Like<T>) =>
    plainToInstance(ClassCtor, data, {
      targetMaps,
    })
}

/**
 * Formats a URL with the given parameters and query.
 * @param url URL to format
 * @param params Parameters to include in the URL
 * @param query Query parameters to include in the URL
 * @returns Formatted URL
 */
export function getRouter(
  url: string,
  params?: Record<string, any>,
  query?: Record<string, any>
): string {
  // 先处理路由参数替换
  let finalUrl = url
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      finalUrl = finalUrl.replace(`{${key}}`, value)
    })
  }

  // 创建完整URL
  const u = new URL(finalUrl, process.env.NEXT_PUBLIC_DOMAIN)

  // 添加查询参数
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        u.searchParams.append(key, value)
      }
    })
  }
  return u.href
}

/**
 * Gets the dimensions and position of an element.
 * @param element HTMLElement to get information from
 * @description Returns the dimensions and position of the element relative to the viewport.
 * @returns Object containing the element's dimensions and position
 */
export function getElementInfo(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  return {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    winTop: rect.top + window.scrollY,
    winLeft: rect.left + window.scrollX,
    winRight: rect.right + window.scrollX,
    winBottom: rect.bottom + window.scrollY,
  }
}

/**
 * Stops the propagation of a mouse event and prevents its default action.
 * @param event Mouse event to stop propagation for
 */
export function stopPropagation(event: React.MouseEvent) {
  event.stopPropagation()
  event.preventDefault()
  return false
}

export function toPlainObject<T>(data: T[]): T[]
export function toPlainObject<T>(data: T): T
export function toPlainObject<T>(data: T | T[]) {
  if (Array.isArray(data)) {
    return data.map((item) => instanceToPlain(item))
  }
  return instanceToPlain(data)
}

/**
 * 把object key由下划线转成驼峰
 * @param {object} data
 * @returns object
 */
export function object2camelCase<T>(data: T[]): T[]
export function object2camelCase<T>(data: T): T
export function object2camelCase<T extends Record<string, any>>(
  data: T | T[]
): T | T[] {
  if (Array.isArray(data)) {
    return data.map((item) => object2camelCase(item) as T)
  } else if (isPlainObject(data)) {
    const res: Record<string, T> = {}
    each(data, (val, key) => {
      res[camelCase(key)] = object2camelCase(val as T)
    })
    return res as T
  } else {
    return data
  }
}

/**
 * 格式化日期
 * @param date
 * @param format
 * @returns
 */
export function formatDate(
  date: dayjs.ConfigType,
  format: string = 'YYYY-MM-DD'
) {
  return dayjs(date).format(format)
}

/**
 * 格式化文本，替换换行符为<br/>
 * @param text 需要格式化的文本
 * @returns 格式化后的文本
 */
export function formatBreakText(text?: string) {
  return text ? text.replace(/(?:\r\n|\n|\r)/g, '<br/>') : ''
}

/**
 * 格式化 slug，去除开头的斜杠
 * @param slug 需要格式化的 slug
 * @returns 格式化后的 slug
 */
export function normalizeSlug(slug: string) {
  return slug.startsWith('/') ? slug.slice(1) : slug
}

/**
 * 获取站点Logo的URL和尺寸信息
 */
export function getSiteLogo() {
  return {
    url: process.env.NEXT_PUBLIC_LOGO_URL as string,
    width: 600,
    height: 240,
  }
}

/**
 * 将菜单数据转换为Banner实体
 * @param menus 菜单数据
 * @returns
 */
export function menusToBanner(menus: Menus): BannerEntity {
  const d: BannerEntity = {
    title: menus.name,
    brief: menus.brief,
    fullScreen: menus.adGroupDetail?.fullScreen || false,
    carousel: null,
  }
  if (menus.adGroupDetail?.adItems?.length) {
    d.carousel = menus.adGroupDetail.adItems.map(
      (item) =>
        ({
          video: item.videoUrl || '',
          cover: item.cover,
          mobileCover: item.mobileCover,
          title: item.title,
          titleColor: item.titleColor,
          brief: item.brief,
          briefColor: item.briefColor,
        }) as BannerCarousel
    )
  }
  return toPlainObject(formatInstance(BannerEntity, BannerTarget)(d))
}

/**
 * 图片修改尺寸
 * @param {string} src 图片地址
 * @param {number} width width
 * @param {number} height height
 * @param {string} m module
 * @returns src
 */
const ossFlag = 'x-oss-process'
export function imageSize(
  src: string,
  width?: number,
  height?: number,
  m = 'fill'
) {
  if (!src || /\.svg$/.test(src)) return src
  if (src.includes('googleapis')) return src

  if (src.includes(ossFlag)) {
    let res = [src]
    if (width || height) res = [`${src}/resize`]
    if (width) res.push(`w_${width}`)
    if (height) res.push(`h_${height}`)
    if (width && height) res.push(`m_${m}`)
    return res.join(',') + '/format,webp'
  } else {
    const res = [src]
    if (width) res.push(`w=${width}`)
    if (height) res.push(`h=${height}`)
    return res.join('&').replace(/&/, '?')
  }
}

/**
 * Generate article metadata
 * @param data Article or CaseStudies data
 * @param param1
 * @returns Metadata object
 */
export function articleMetadata(
  data: Article | CaseStudies,
  {
    articleSlug,
    caseStudiesSlug,
  }: {
    articleSlug?: string
    caseStudiesSlug?: string
  }
): Metadata {
  const keywords: string[] = []
  keywords.push(
    ...[...(data.tags || []), ...(data.industries || [])].map((tag) => tag.name)
  )
  if (data.location) {
    keywords.push(data.location)
  }
  const res: Metadata = {
    title: data.subject,
    keywords,
    description: data.brief,
    alternates: {
      canonical: (function () {
        if (articleSlug) {
          return getArticleDetailHref(articleSlug)
        }
        if (caseStudiesSlug) {
          return getCaseStudiesDetailHref(caseStudiesSlug)
        }
      })(),
    },
    openGraph: {
      title: data.subject,
      description: data.brief,
      images: (function () {
        if (data.heroSrc || data.coverSrc) {
          return {
            url: imageSize(data.heroSrc || data.coverSrc, 1200, 630),
            width: 1200,
            height: 630,
          }
        }
        return getSiteLogo()
      })(),
    },
  }
  return res
}

/**
 * 获取团队头像
 * @param team Team实体
 * @returns
 */
export function getTeamAvatar(team: Team, detail = false) {
  return team.recommend && !detail
    ? team.recommendCoverSrc || team.coverSrc
    : team.coverSrc || team.recommendCoverSrc
}
